import React, {useCallback, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useAtom} from "jotai";
import {useTranslation} from "react-i18next";
import clsx from "clsx";
import Countdown, {CountdownRenderProps} from "react-countdown";
import st from "@/styles/LoginPage.module.scss";
import rst from "@/styles/ResetPinPage.module.scss";
import DashboardHeader from "@/ui/DashboardHeader.tsx";
import Bottom from "@/ui/Bottom.tsx";
import Button from "@/ui/Button.tsx";
import WritePinFields from "@/ui/WritePinFields.tsx";
import WritePwdFields from "@/ui/WritePwdFields.tsx";
import LoginLoader from "@/ui/LoginLoader.tsx";
import ResendCounter from "@/ui/ResendCounter.tsx";
import {TextField} from "@mui/material";
import ChevroneIcon from "@/svg/ChevronIocn.tsx";
import EmailIcon from "@/svg/EmailIcon.tsx";
import PhoneIcon from "@/svg/PhoneIcon.tsx";
import useAuth from "@/hooks/auth.ts";
import useIntervals from "@/hooks/intervals.ts";
import CommonClass from "@/utils/common.ts";
import DashboardClass from "@/utils/dashboard_class.ts";
import eventEmitter from "@/utils/event_emmiter_instance.ts";
import {pin_value, pwd_value, resend_interval} from "@/atoms/common.ts";
import {login_data} from "@/atoms/profile.ts";
import {codeType} from "@/types_and_interfaces/types.ts";
import LoginData from "@/types_and_interfaces/login_data.ts";
import {CheckPhoneData} from "@/types_and_interfaces/check_phone_data_interface.ts";
import {country_code} from "@/utils/constants.ts";
import {textfield_sx} from "@/utils/mui_sx.ts";
import MainLoginForm from "@/pages/login/elemsV2/MainLoginForm.tsx";


const ResetPinPage: React.FC = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const auth = useAuth();
    const intervals_hook = useIntervals(resend_interval);

    const [loginData] = useAtom(login_data);
    const [pwd_fields] = useAtom(pwd_value);
    const [otp_fields] = useAtom(pin_value);
    const [interval, set_interval] = useAtom(resend_interval);

    const [receive_type, set_receive_type] = useState<codeType>("");
    // step 1 = select type, step 1.5 = input email/phone, step 2 = otp + new pin
    const [step, set_step] = useState<number>(1);
    const [otp_error, set_otp_error] = useState<boolean>(false);
    const [loading, set_loading] = useState<boolean>(false);

    // Input fields for email/phone when needed
    const [inputEmail, setInputEmail] = useState<string>("");
    const [inputPhone, setInputPhone] = useState<string>("");
    const [inputError, setInputError] = useState<boolean>(false);

    // Store the actual value used for reset (either from login_data or input)
    const [resetValue, setResetValue] = useState<string>("");

    const is_interval = Boolean(interval !== null && interval.length);
    const data: LoginData = Boolean(loginData) ? JSON.parse(loginData) : {};

    // Get check phone data from session storage
    const getCheckPhoneData = (): CheckPhoneData | null => {
        const stored = sessionStorage.getItem(CommonClass.local_storage_fields.check_phone_data);
        if (stored) {
            try {
                return JSON.parse(stored) as CheckPhoneData;
            } catch {
                return null;
            }
        }
        return null;
    };

    const checkPhoneData = getCheckPhoneData();

    // Determine what we have from login_data
    const loginType = data?.type || "";
    const loginValue = data?.value || "";

    const set_loading_handler = useCallback((x: boolean) => {
        set_loading(x);
    }, []);

    const set_otp_err_handler = useCallback((x: boolean) => {
        set_otp_error(x);
    }, []);

    const handleBack = () => {
        if (step === 1.5) {
            set_step(1);
            setInputEmail("");
            setInputPhone("");
            setInputError(false);
            set_receive_type("");
        } else if (step === 2) {
            set_step(1);
            setResetValue("");
            set_receive_type("");
        } else {
            eventEmitter.emit(CommonClass.CustomEvents.reset_otp_value);
            eventEmitter.emit(CommonClass.CustomEvents.reset_pwd_value);
            navigate(DashboardClass.login_routes.LoginStep1);
        }
    };

    const resetHandlerStepTwo = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const otp = otp_fields.join("");
        const pwd = pwd_fields.join("");
        const user_id = getCheckPhoneData()?.user_id || "";

        const success = await auth.resetPwdSt2NotLogged(resetValue, otp, pwd, receive_type, user_id);

        if (success) {
            await auth.auth(pwd, user_id)
            eventEmitter.emit(CommonClass.CustomEvents.reset_otp_value);
            eventEmitter.emit(CommonClass.CustomEvents.reset_pwd_value);
            // navigate(DashboardClass.login_routes.LoginStep1);
        }
    };

    const handleSelectType = (type: codeType) => {
        set_receive_type(type);
        setInputError(false);

        if (type === loginType && loginValue) {
            const value = type === "phone" ? CommonClass.phoneFormater(loginValue) : loginValue;
            proceedToStepTwo(type, value);
        } else {
            set_step(1.5);
        }
    };

    // Called when user submits the input form (step 1.5)
    const handleInputSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        let value = receive_type === "email" ? inputEmail : inputPhone;

        if (!value.length) {
            setInputError(true);
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("complete_field_err"));
            return;
        }

        // Validate based on type
        if (receive_type === "email" && !CommonClass.isEmailValid(value)) {
            setInputError(true);
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("valid_email_err"));
            return;
        }

        if (receive_type === "phone" && !CommonClass.phoneNumberValidate(value)) {
            setInputError(true);
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("valid_nr_err"));
            return;
        }

        // Format phone number with country code
        if (receive_type === "phone") {
            value = CommonClass.phoneFormater(value);
        }

        setInputError(false);
        await proceedToStepTwo(receive_type, value);
    };

    const proceedToStepTwo = async (type: codeType, value: string) => {
        const user_id = getCheckPhoneData()?.user_id || "";
        const reset = await auth.resetPwdSt1NotLogged(type, value, user_id);

        if (reset) {
            setResetValue(value);
            set_step(2);
            eventEmitter.emit(CommonClass.CustomEvents.set_resend_interval, false);
            intervals_hook.setIntervalHandler();
        }
    };

    const handleResend = async () => {
        const user_id = getCheckPhoneData()?.user_id || "";
        if (resetValue && receive_type) {
            await auth.resetPwdSt1NotLogged(receive_type, resetValue, user_id);
            eventEmitter.emit(CommonClass.CustomEvents.set_resend_interval, false);
            intervals_hook.setIntervalHandler();
        }
    };

    const buttons = [
        {
            label: "phone",
            icon: <PhoneIcon />,
            value: t("phone1"),
            onClick: () => handleSelectType("phone"),
            visible: checkPhoneData?.can_reset_by_phone
        },
        {
            label: "email",
            icon: <EmailIcon />,
            value: t("email_address"),
            onClick: () => handleSelectType("email"),
            visible: checkPhoneData?.has_email && checkPhoneData?.can_reset_by_email
        }
    ];



    useEffect(() => {

        if (!data?.value) {
            navigate(DashboardClass.login_routes.LoginStep1);
        }
    }, []);

    useEffect(() => {
        eventEmitter.on(CommonClass.CustomEvents.set_req_load, set_loading_handler);
        eventEmitter.on(CommonClass.CustomEvents.set_err_otp, set_otp_err_handler);

        return () => {
            eventEmitter.off(CommonClass.CustomEvents.set_req_load, set_loading_handler);
            eventEmitter.off(CommonClass.CustomEvents.set_err_otp, set_otp_err_handler);
        };
    }, [set_loading_handler, set_otp_err_handler]);

    return (
        <main className={clsx(st.main, st.login_form_contaienr)}>
            <DashboardHeader />

            <div className={st.login_form_contaienr}>
                <div className={clsx(st.log_vars, rst.reset_pin_container)}>

                    {step === 1 && (
                        <div className={rst.warning_message}>
                            {t("pin_mistyped_warning")}
                        </div>
                    )}

                    <div className={rst.heading}>
                        <h4>{t("reset_pwd")}</h4>

                        {step === 1 && (
                            <p className={rst.subtitle}>
                                {t("select_otp_type")}
                            </p>
                        )}

                        {step === 1.5 && (
                            <p className={rst.subtitle}>
                                {receive_type === "email" ? t("enter_your_email") : t("enter_your_phone")}
                            </p>
                        )}
                    </div>

                    {/* Step 1: Select reset type */}
                    {step === 1 && (
                        <div className={rst.content}>
                            {buttons.map((x, i) => {
                                return x.visible && (
                                    <button
                                        disabled={is_interval}
                                        type="button"
                                        className={rst.otp_type_btn}
                                        key={`select_reset_pin_otp_${x.label + i}`}
                                        onClick={x.onClick}
                                    >
                                        <div className={rst.left}>
                                            {x.icon}
                                            {x.value}
                                        </div>
                                        <ChevroneIcon className={rst.chevrone} />
                                    </button>
                                );
                            })}

                            {interval !== null && interval.length ? (
                                <div className={rst.countdown}>
                                    {t("resend_in")}
                                    <Countdown
                                        date={JSON.parse(interval).interval}
                                        renderer={(args) => renderer({...args})}
                                        intervalDelay={1000}
                                        zeroPadTime={2}
                                        onComplete={() => {
                                            set_interval("");
                                        }}
                                    />
                                </div>
                            ) : null}
                        </div>
                    )}

                    {/* Step 1.5: Input email or phone */}
                    {step === 1.5 && (
                        <div className={rst.content}>
                            <form onSubmit={handleInputSubmit} autoComplete="off">
                                <div className={rst.labels}>
                                    {/*<span>*/}
                                    {/*    {receive_type === "email" ? t("email_address") : t("phone1")}*/}
                                    {/*</span>*/}
                                    {receive_type === "email" ? (
                                        <TextField
                                            label="Email"
                                            name={"email"}
                                            error={inputError}
                                            sx={textfield_sx}
                                            color="success"
                                            placeholder={"email.address@email.com"}
                                            value={inputEmail}
                                            onChange={(e) => setInputEmail(e.target.value)}
                                        />
                                    ) : (
                                        <div className={rst.phone_input_row}>
                                            <span className={rst.country_code}>{country_code}</span>
                                            <TextField
                                                name="phone"
                                                type="tel"
                                                inputMode="numeric"
                                                placeholder="XXXXXXXX"
                                                value={inputPhone}
                                                onChange={(e) => setInputPhone(CommonClass.formatNumber(e.target.value))}
                                                error={inputError}
                                                // maxLength={8}
                                                className={rst.input_field}
                                            />

                                        </div>
                                    )}
                                </div>

                                <div className={rst.controller_row}>
                                    <Button
                                        outlined
                                        type="button"
                                        className={rst.back}
                                        onClick={handleBack}
                                    >
                                        <ChevroneIcon />
                                        {t("back")}
                                    </Button>

                                    <Button
                                        className={rst.submit}
                                        type="submit"
                                    >
                                        {t("continue")}
                                    </Button>
                                </div>
                            </form>
                        </div>
                    )}

                    {step === 2 && (
                        <div className={rst.content}>
                            <p className={rst.info}>
                                {receive_type === "email"
                                    ? t("reset_pin_code_sent_to_email", {value: resetValue})
                                    : t("reset_pin_code_sent_to_phone", {value: resetValue})}
                            </p>

                            <form onSubmit={resetHandlerStepTwo} autoComplete="off">
                                <div className={rst.labels}>
                                    <span>{t("write_otp")}</span>
                                    <WritePinFields
                                        error={otp_error}
                                        className={rst.pin}
                                    />
                                </div>

                                <div className={rst.labels}>
                                    <span>{t("write_new_pwd")}</span>
                                    <WritePwdFields className={rst.pwd} />
                                </div>

                                <div className={rst.controller_row}>
                                    <Button
                                        outlined
                                        type="button"
                                        className={rst.back}
                                        onClick={() => set_step(1)}
                                        disabled={is_interval}
                                    >
                                        <ChevroneIcon />
                                        {t("back")}
                                    </Button>

                                    <Button
                                        className={rst.submit}
                                        type="submit"
                                    >
                                        {t("set_pin_short")}
                                    </Button>
                                </div>
                            </form>

                            <ResendCounter
                                onClick={handleResend}
                                interval_atom={resend_interval}
                            />
                        </div>
                    )}

                    {loading && <LoginLoader />}
                </div>

                {/*<div className={clsx(st.log_vars, st.secondary_second_steps)}>*/}
                    <MainLoginForm checkPhoneData={getCheckPhoneData()} />
                {/*</div>*/}
            </div>




            <Bottom />
        </main>
    );
};

const renderer = (args: CountdownRenderProps) => {
    const {minutes, seconds} = args;
    const padValue = (value: number) => (value < 10 ? `0${value}` : `${value}`);

    return (
        <div>
            {padValue(minutes) || "00"} :
            {" " + padValue(seconds) || " 00"}
        </div>
    );
};

export default ResetPinPage;
